import Tool, { type ToolButtonComponent } from './Tool';
import React, { useRef, useState, useEffect } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { isMindMap } from '../objects/guards';
import type MindMap from '../objects/MindMap';
import { type MindMapSides } from '../objects/MindMap';
import orientationHorizontalSVG from '../assets/icons/orientationHorizontal.svg';
import orientationVerticalSVG from '../assets/icons/orientationVertical.svg';
import { svgToIcon } from '../utils/svg';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import classNames from 'classnames';
import styles from '../styles.module.css';
import { type Instance as TippyInstance } from 'tippy.js/index';

const orientationOptions: { name: string; sides: MindMapSides[] }[] = [
  { name: 'Horizontal', sides: ['east', 'west'] },
  { name: 'Vertical', sides: ['north', 'south'] },
];

function iconForOrientation(sides: MindMapSides[]) {
  const isHorizontal =
    sides.length === 2 && sides.includes('east') && sides.includes('west');
  return svgToIcon(
    isHorizontal ? orientationHorizontalSVG : orientationVerticalSVG
  );
}

function getCurrentOrientation(sides: MindMapSides[]) {
  return orientationOptions.find(
    option =>
      option.sides.length === sides.length &&
      option.sides.every(side => sides.includes(side))
  );
}

const Popup: React.FC<{
  mindmap: MindMap;
  currentOrientation: (typeof orientationOptions)[0] | undefined;
  setCurrentOrientation: (orientation: (typeof orientationOptions)[0]) => void;
}> = ApplicationView(
  ({ mindmap, currentOrientation, setCurrentOrientation }) => {
    const controller = useController(WhiteboardController);

    const setOrientation = (orientation: (typeof orientationOptions)[0]) => {
      setCurrentOrientation(orientation);
      mindmap.setSides(orientation.sides);
      Tool.toolActionComplete({ controller });
    };

    return (
      <ToolbarMenu columns={2}>
        {orientationOptions.map(option => (
          <ToolButton
            key={option.name}
            className={classNames({
              [styles.toolButtonActive]:
                currentOrientation?.name === option.name,
            })}
            onClick={() => setOrientation(option)}
          >
            {iconForOrientation(option.sides)}
          </ToolButton>
        ))}
      </ToolbarMenu>
    );
  }
);

const OrientationButton: ToolButtonComponent = ApplicationView(
  ({ tool, controller }) => {
    const { selection: mindmap } = controller;
    if (!isMindMap(mindmap)) return null;

    const tippyRef = useRef<TippyInstance>();
    const currentSides = mindmap.sides;
    const [currentOrientation, setCurrentOrientation] = useState(
      getCurrentOrientation(currentSides)
    );

    // Update orientation when the mindmap's sides change
    useEffect(() => {
      setCurrentOrientation(getCurrentOrientation(currentSides));
    }, [currentSides]);

    return (
      <ToolButton
        popup={
          <Popup
            mindmap={mindmap}
            currentOrientation={currentOrientation}
            setCurrentOrientation={setCurrentOrientation}
          />
        }
        tippyRef={tippyRef}
        id='mindmapOrientation'
        tool={tool}
      >
        {iconForOrientation(currentSides)}
      </ToolButton>
    );
  }
);

class ToolMindMapOrientation extends Tool {
  static toolName = 'Orientation';

  static action() {}

  static enabled() {
    return true;
  }

  static buttonComponent() {
    return OrientationButton;
  }
}

export default ToolMindMapOrientation;
